import { useCallback, useMemo, useState } from "react";
import { Column, TableInstance } from "react-table";
import { useNavigate } from "react-router-dom";
import { Table } from "../../components/table/Table";
import { IUserExtended } from "../../models/User";
import { SelectColumnFilter } from "../../components/table/filters/SelectColumnFilter";
import CSVExport from "../../components/export/CSVExport";

const UserManagementTable = () => {
  
  let navigate = useNavigate();

  const [exportList, setExportList] = useState<boolean>(false);
  const [exportData, setExportData] = useState<{ headers: string[]; data: Record<string, any>[] }>({
      headers: [],
      data: [],
  });

  const columns: Column<IUserExtended>[] = useMemo(
    () => [
      {
        Header: "Username",
        accessor: "username",
        width: 180,
        minWidth: 300,
      },
      {
        Header: "Email",
        accessor: "email",
        width: 180
      },
      {
        Header: "Last Name",
        accessor: "lastName",
      },
      {
        Header: "First Name",
        accessor: "firstName",
      },
      {
        Header: "Dealer",
        accessor: "dealerName",
        Cell: ({cell}: any) => {
          const isLegacyDealer = cell.row.original.dealerId !== null && cell.row.original.isLegacyDealer;
          return (
            <>
            <div>{cell.value}</div>
            {isLegacyDealer && (
              <div className="column-note" style={{ fontSize: "smaller" }}>Legacy Dealer</div>
            )}
            </>
          )
        },
        Filter: SelectColumnFilter
      },
      {
        Header: "Legacy Dealer",
        accessor: "isLegacyDealer",
        Filter: SelectColumnFilter
      },
      {
        Header: "Role",
        accessor: "role",
        width: 70,
        Filter: SelectColumnFilter,
      },
      {
        Header: "Sales Rep Code",
        accessor: "salesRepCode",
        width: 100,
        Cell: ({cell}: any) => {
          const isSecondary = cell.value !== "" && !!cell.row.original.secondarySalesRepCode;
          return (
            <>
            <div>{cell.value}</div>
            {isSecondary && (
              <div className="column-note" style={{ fontSize: "smaller" }}>Secondary: {cell.row.original.secondarySalesRepCode}</div>
            )}
            </>
          )
        },
        Filter: SelectColumnFilter
      },
      {
        Header: "Secondary Sales Rep",
        accessor: "secondarySalesRepCode",
        width: 100,
        Filter: SelectColumnFilter,
      },
      {
        Header: "Status",
        accessor: "status",
        width: 70,
        Filter: SelectColumnFilter,
      }
    ],
    []
  );

  const AddRow = useCallback(
    (instance: TableInstance<IUserExtended>) => () => {
      navigate("/user/add", { state: { user: null } });
    },
    []
  );

  const ViewRow = useCallback(
    (instance: TableInstance<IUserExtended>) => () => {
      const user = instance.selectedFlatRows[0].original;
      navigate("/user/view", { state: { user: user } });
    },
    []
  );

  const onExport = useCallback(
    (instance: TableInstance<IUserExtended>) => () => {
        const headers = instance.allColumns
            .filter(column => column.id !== "_selector")
            .map(column => (typeof column.Header === "string" ? column.Header : String(column.id)));

        if(instance.selectedFlatRows.length === 0) {
            const data = instance.filteredFlatRows.map(row => {
              return instance.allColumns.reduce((acc, column) => {
                  if (column.id !== "_selector") {
                      acc[column.Header as string] = row.values[column.id];
                  }
                  return acc;
              }, {} as Record<string, any>);
            });

            setExportData({ headers, data });
        }
        else {
          const data = instance.selectedFlatRows.map(row => {
              return instance.allColumns.reduce((acc, column) => {
                  if (column.id !== "_selector") {
                      acc[column.Header as string] = row.values[column.id];
                  }
                  return acc;
              }, {} as Record<string, any>);
          });

          setExportData({ headers, data });
        }

        setExportList(true);
  },[]);

  const handleExportComplete = () => {
    setExportList(false);
  };

  return (
    <div>
      <Table<IUserExtended>
        name="user-list-table"
        serverOnChange={false}
        columns={columns}
        apiGet="/usermanagement"
        hiddenColumns={["isLegacyDealer", "secondarySalesRepCode"]}
        initialFilters= {[
          {
            id: "isLegacyDealer",
            value: false
          }
        ]}
        onAdd={AddRow}
        onEdit={ViewRow}
        onExport={onExport}
      />
      {exportList && <CSVExport list={exportData} fileNamePrefix="User_List" onExportComplete={handleExportComplete}  />}
    </div>
  );
};

export default UserManagementTable;